import { Component, OnInit, inject, DestroyRef, effect } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, NavigationEnd, Router, RouterLink } from '@angular/router';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { NgxUiLoaderService } from 'ngx-ui-loader';
import { GenericService } from '../services/generic.service';
import { MsalLoginService } from '../services/msal.service';
import { Meta, Title } from '@angular/platform-browser';
import { HeadScriptRemoverService } from '../services/head-script-remover.service';
import { SITE_DATA } from '../productization/default.constant';
import { IpDetectorState } from 'src/app/store/ip-detector/ip-detector.state';
import { filter } from 'rxjs';
import { GenericState } from '../store/generic.state';
import { Store } from '@ngxs/store';
import * as _ from 'underscore';
import { SignupQuestionsSignal } from '../signal/signup-questions.signal';
import { distinctUntilChanged, combineLatestWith } from 'rxjs/operators';
import { LaunchDarklyState } from '../store/launch-darkly/launch-darkly.state';
import { HeroComponent } from './hero/hero.component';
import { JobListingComponent } from './job-listing/job-listing.component';
import { CategoriesComponent } from './categories/categories.component';
import { VideoHolderComponent } from './video-holder/video-holder.component';
import { CareersLifeComponent } from './careers-life/careers-life.component';
import { RecruitTipsComponent } from './recruit-tips/recruit-tips.component';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzSpaceModule } from 'ng-zorro-antd/space';
import { NzButtonModule } from 'ng-zorro-antd/button';

@Component({
    selector: 'app-lander',
    templateUrl: './lander.component.html',
    styleUrls: ['./lander.component.scss'],
    imports: [HeroComponent, JobListingComponent, CategoriesComponent, VideoHolderComponent, CareersLifeComponent, RecruitTipsComponent, NzModalModule, NzIconModule, NzSpaceModule, NzButtonModule, RouterLink]
})
export class LanderComponent implements OnInit {
	private readonly route = inject(ActivatedRoute);
	private readonly router = inject(Router);
	private readonly destroyRef = inject(DestroyRef);
	private readonly ngxLoader = inject(NgxUiLoaderService);
	private readonly notification = inject(NzNotificationService);
	private readonly genericService = inject(GenericService);
	private readonly msalLoginService = inject(MsalLoginService);

	private readonly metaService = inject(Meta);
	private readonly titleService = inject(Title);
	private readonly headScript = inject(HeadScriptRemoverService);
	private readonly store = inject(Store);
	private readonly signupQuestionsSignal = inject(SignupQuestionsSignal);
	private readonly getIpAddress = this.store.select(IpDetectorState.getIp);
	private readonly genericLookup = this.store.select(GenericState.getGeneric);
	private readonly launchDarklyResult = this.store.select(LaunchDarklyState.getFlags);
	private readonly launchDarklyFlags = toSignal(this.launchDarklyResult, { initialValue: null });
	
	public setCountryOrigin: string | null = null;

	public viewportHeight: any;
	public siteData = SITE_DATA;
	public isShowModal: boolean = false;
	public getOnboardingData: boolean = false;

	constructor() {
		const router = this.router;

		router.events.subscribe((event) => {
			if (event instanceof NavigationEnd) {
				const url = event.url.split('/');
				if (url[1] === 'verify') {
					this.ngxLoader.start();
					this.genericService
						.postWithOutPayload(
							`Applicant/VerifyAccount/${this.route.snapshot.params['code']}`
						)
						.subscribe({
							next: () => {
								this.notification.blank(
									this.siteData.siteName,
									'Your account is now verified. You can now login to your account.'
								);
								this.ngxLoader.stop();
								this.router.navigate(['/']);
								this.msalLoginService.msalLogin();
							},
							error: () => {
								this.ngxLoader.stop();
								this.router.navigate(['/']);
							}
						});
				}
			}
		});

		effect(() => {
			const signupState = this.signupQuestionsSignal.state();
			const flags = this.launchDarklyFlags();
			
			// Skip if signup state hasn't been loaded yet (mimics skip(1))
			const hasLoadedState = Object.keys(signupState.rolesExperience || {}).length > 0;
			if (flags && hasLoadedState && !localStorage.getItem('candidateOnboarding')) {
				localStorage.setItem('candidateOnboarding', String(flags.onboarding.enabled));
				this.isShowModal = !signupState.isProfileDetailsCompleted && flags.onboarding.enabled;
			}
		});
	}

	ngOnInit(): void {
		this.viewportHeight = Math.min(window.innerHeight, 1200) + 'px';

		this.getIpAddress.pipe(
			takeUntilDestroyed(this.destroyRef),
			filter((e: any) => !_.isEmpty(e)), 
			distinctUntilChanged((prev, curr) => _.isEqual(prev, curr)),
			combineLatestWith(this.genericLookup))
		.subscribe(([countryOrigin, countryList]) => {
			if (this.siteData.defaultCountry === 'none') {
				if (countryList.lookUpCountries?.includes(countryOrigin.country_name)) {
					this.setCountryOrigin = countryOrigin.country_name;
				}
			} else {
				this.setCountryOrigin = this.siteData.defaultCountry;
			}
		});

		this.headScript.removeLdJsonScripts();
		this.metaService.updateTag({
			name: 'description',
			content:
				'Experience an extraordinary global career at Cloudstaff, the #1 workplace everywhere. Join our talented team and be part of something bigger. Apply now!'
		});

	}
}
